import { z } from "zod";

export const itemTypeSchema = z.enum([
  "sticky-note",
  "document",
  "label",
  "image",
  "video",
  "pdf",
  "spreadsheet",
  "web-clip",
  "date-event",
  "stack",
  "arrow",
  "frame"
]);
export type ItemType = z.infer<typeof itemTypeSchema>;

export const themeSchema = z.enum(["light", "dark", "system"]);
export type Theme = z.infer<typeof themeSchema>;
export const assetStatusSchema = z.enum(["local", "uploading", "ready", "failed"]);
export type AssetStatus = z.infer<typeof assetStatusSchema>;
export const clipStatusSchema = z.enum(["pending", "ready", "failed"]);
export type ClipStatus = z.infer<typeof clipStatusSchema>;
export const stickyColorSchema = z.enum(["paper", "cyan", "green", "orange", "blue", "yellow"]);
export type StickyColor = z.infer<typeof stickyColorSchema>;
export const dateEventColorSchema = z.enum(["lilac", "sky", "mint", "butter", "peach", "rose"]);
export type DateEventColor = z.infer<typeof dateEventColorSchema>;
export const documentColorSchema = z.enum(["paper", "lilac", "sky", "mint", "butter", "peach"]);
export type DocumentColor = z.infer<typeof documentColorSchema>;
export const folderColorSchema = z.enum(["lilac", "sage", "sky", "sand", "clay", "stone"]);
export type FolderColor = z.infer<typeof folderColorSchema>;
export const arrowColorSchema = z.enum(["ink", "blue", "green", "orange", "red", "violet"]);
export type ArrowColor = z.infer<typeof arrowColorSchema>;
export const frameColorSchema = z.enum(["clear", "blue", "green", "yellow", "lilac", "clay"]);
export type FrameColor = z.infer<typeof frameColorSchema>;
export const frameBackgroundStyleSchema = z.enum(["none", "subtle", "soft"]);
export type FrameBackgroundStyle = z.infer<typeof frameBackgroundStyleSchema>;
export const frameBorderStyleSchema = z.enum(["solid", "dashed", "dotted"]);
export type FrameBorderStyle = z.infer<typeof frameBorderStyleSchema>;
export const frameBorderColorSchema = z.enum(["match", "ink", "blue", "green", "yellow", "lilac", "clay"]);
export type FrameBorderColor = z.infer<typeof frameBorderColorSchema>;

export const hybridClockSchema = z.object({
  wallTime: z.number().int().nonnegative(),
  counter: z.number().int().nonnegative(),
  deviceId: z.string().min(1).max(128)
});
export type HybridClock = z.infer<typeof hybridClockSchema>;

const base64Schema = z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/, "Expected base64 data");

/**
 * Encrypted body of a PIN-locked note or document. The PIN never leaves the device;
 * only the salt, IV, and AES-GCM ciphertext are stored and synced.
 */
export const noteLockSchema = z.object({
  version: z.literal(1),
  iterations: z.number().int().min(100_000).max(10_000_000),
  salt: base64Schema.min(16).max(64),
  iv: base64Schema.min(16).max(24),
  ciphertext: base64Schema.min(24).max(16 * 1024 * 1024),
  /**
   * Absent for a note locked on its own, where only the body is encrypted. "item" marks
   * content sealed by its folder (every identifying field is encrypted), and "folder" marks
   * the folder's own PIN check.
   */
  scope: z.enum(["item", "folder"]).optional(),
  /** A locked folder's public key: anything can be sealed into the folder without its PIN. */
  publicKey: base64Schema.min(80).max(120).optional(),
  /** The one-time public key a sealed item's key was agreed with. */
  ephemeralKey: base64Schema.min(80).max(120).optional()
}).strict();
export type NoteLock = z.infer<typeof noteLockSchema>;
export const SEALED_ASSET_MIME_TYPE = "application/vnd.bedrock.sealed";

const markdownFields = {
  markdown: z.string(),
  plainText: z.string(),
  lock: noteLockSchema.optional()
};

export const stickyNotePropsSchema = z.object({
  kind: z.literal("sticky-note"),
  ...markdownFields,
  accent: stickyColorSchema.default("paper")
});
export const documentPropsSchema = z.object({
  kind: z.literal("document"),
  title: z.string(),
  ...markdownFields,
  color: documentColorSchema.default("paper")
});
export const labelPropsSchema = z.object({
  kind: z.literal("label"),
  text: z.string(),
  size: z.enum(["small", "medium", "large"]).default("medium"),
  lock: noteLockSchema.optional()
});
export const imagePropsSchema = z.object({
  kind: z.literal("image"),
  assetId: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  alt: z.string().default(""),
  status: assetStatusSchema,
  localUrl: z.string().optional(),
  lock: noteLockSchema.optional()
});
export const videoPropsSchema = z.object({
  kind: z.literal("video"),
  assetId: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  status: assetStatusSchema,
  posterAssetId: z.string().nullable(),
  localUrl: z.string().optional(),
  lock: noteLockSchema.optional()
});
export const pdfPropsSchema = z.object({
  kind: z.literal("pdf"),
  assetId: z.string(),
  filename: z.string(),
  mimeType: z.literal("application/pdf"),
  status: assetStatusSchema,
  localUrl: z.string().optional(),
  lock: noteLockSchema.optional()
});
export const spreadsheetMimeTypeSchema = z.enum([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel"
]);
export type SpreadsheetMimeType = z.infer<typeof spreadsheetMimeTypeSchema>;
export const spreadsheetPropsSchema = z.object({
  kind: z.literal("spreadsheet"),
  assetId: z.string(),
  filename: z.string(),
  mimeType: spreadsheetMimeTypeSchema,
  status: assetStatusSchema,
  localUrl: z.string().optional(),
  lock: noteLockSchema.optional()
});
export const webClipPropsSchema = z.object({
  kind: z.literal("web-clip"),
  clipId: z.string(),
  url: z.url(),
  title: z.string(),
  description: z.string(),
  siteName: z.string(),
  status: clipStatusSchema,
  previewAssetId: z.string().nullable(),
  previewUrl: z.string().optional(),
  lock: noteLockSchema.optional()
});
const calendarDateSchema = z.string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Event date must use YYYY-MM-DD")
  .refine((value) => {
    const [year = 0, month = 0, day = 0] = value.split("-").map(Number);
    const candidate = new Date(year, month - 1, day, 12);
    return candidate.getFullYear() === year && candidate.getMonth() === month - 1 && candidate.getDate() === day;
  }, "Event date must be a real calendar date");
export const dateEventPropsSchema = z.object({
  kind: z.literal("date-event"),
  title: z.string().trim().min(1).max(200),
  date: calendarDateSchema,
  time: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, "Event time must use HH:mm").optional(),
  color: dateEventColorSchema.default("lilac"),
  lock: noteLockSchema.optional()
});
export const stackPropsSchema = z.object({
  kind: z.literal("stack"),
  title: z.string(),
  collapsed: z.boolean(),
  color: folderColorSchema.default("lilac"),
  lock: noteLockSchema.optional()
});
export const arrowPropsSchema = z.object({
  kind: z.literal("arrow"),
  path: z.enum(["straight", "curved"]).default("straight"),
  color: arrowColorSchema.default("ink"),
  stroke: z.enum(["solid", "dashed", "dotted"]).default("solid"),
  head: z.enum(["filled", "outline", "none"]).default("filled")
});
export const framePropsSchema = z.object({
  kind: z.literal("frame"),
  title: z.string().default("New frame"),
  color: frameColorSchema.default("clear"),
  backgroundStyle: frameBackgroundStyleSchema.default("soft"),
  borderStyle: frameBorderStyleSchema.default("solid"),
  borderColor: frameBorderColorSchema.default("match")
});

export const itemPropsSchema = z.discriminatedUnion("kind", [
  stickyNotePropsSchema,
  documentPropsSchema,
  labelPropsSchema,
  imagePropsSchema,
  videoPropsSchema,
  pdfPropsSchema,
  spreadsheetPropsSchema,
  webClipPropsSchema,
  dateEventPropsSchema,
  stackPropsSchema,
  arrowPropsSchema,
  framePropsSchema
]);
export type ItemProps = z.infer<typeof itemPropsSchema>;

export const spaceSchema = z.object({
  id: z.string().min(1),
  title: z.string().max(200),
  theme: themeSchema,
  starred: z.boolean().default(false),
  fieldClocks: z.record(z.string(), hybridClockSchema).default({}),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
  deletedAt: z.number().int().nonnegative().nullable()
});
export type Space = z.infer<typeof spaceSchema>;

export const canvasItemSchema = z.object({
  id: z.string().min(1),
  spaceId: z.string().min(1),
  type: itemTypeSchema,
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().finite().positive().max(100_000),
  height: z.number().finite().positive().max(100_000),
  rotation: z.number().finite().min(-360).max(360),
  zIndex: z.number().int(),
  parentStackId: z.string().nullable(),
  props: itemPropsSchema,
  fieldClocks: z.record(z.string(), hybridClockSchema),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
  deletedAt: z.number().int().nonnegative().nullable()
}).refine((item) => item.type === item.props.kind, {
  message: "Canvas item type must match props kind",
  path: ["props", "kind"]
});
export type CanvasItem = z.infer<typeof canvasItemSchema>;

export const syncOperationSchema = z.object({
  opId: z.string().min(1).max(128),
  deviceId: z.string().min(1).max(128),
  entityType: z.enum(["space", "item", "asset", "clip"]),
  entityId: z.string().min(1).max(128),
  baseRevision: z.number().int().nonnegative().nullable(),
  clock: hybridClockSchema,
  patch: z.record(z.string(), z.unknown())
});
export type SyncOperation = z.infer<typeof syncOperationSchema>;

export const permanentDeletePatchSchema = z.object({
  permanentDelete: z.literal(true)
}).strict();

export const spaceUpdatePatchSchema = z.object({
  title: z.string().max(200).optional(),
  theme: themeSchema.optional(),
  starred: z.boolean().optional(),
  deletedAt: z.number().int().nonnegative().nullable().optional()
}).strict().refine((patch) => Object.keys(patch).length > 0, "A space update must change at least one field");

export const itemUpdatePatchSchema = z.object({
  x: z.number().finite().optional(),
  y: z.number().finite().optional(),
  width: z.number().finite().positive().max(100_000).optional(),
  height: z.number().finite().positive().max(100_000).optional(),
  rotation: z.number().finite().min(-360).max(360).optional(),
  zIndex: z.number().int().optional(),
  parentStackId: z.string().nullable().optional(),
  props: itemPropsSchema.optional(),
  deletedAt: z.number().int().nonnegative().nullable().optional()
}).strict().refine((patch) => Object.keys(patch).length > 0, "An item update must change at least one field");

export const syncChangeSchema = z.object({
  sequence: z.number().int().nonnegative(),
  operation: syncOperationSchema
});
export type SyncChange = z.infer<typeof syncChangeSchema>;

export const recoveredVersionSchema = z.object({
  id: z.string().min(1).max(160),
  itemId: z.string().min(1).max(128),
  markdown: z.string(),
  clock: hybridClockSchema,
  recoveredAt: z.number().int().nonnegative()
});
export type RecoveredVersion = z.infer<typeof recoveredVersionSchema>;

export const snapshotSchema = z.object({
  spaces: z.array(spaceSchema),
  items: z.array(canvasItemSchema),
  latestSequence: z.number().int().nonnegative()
});
export type Snapshot = z.infer<typeof snapshotSchema>;

export const pushRequestSchema = z.object({
  deviceId: z.string().min(1).max(128),
  operations: z.array(syncOperationSchema).max(500)
});
export const pushResponseSchema = z.object({
  acknowledgedOperationIds: z.array(z.string()),
  conflicts: z.array(z.object({
    entityId: z.string(),
    losingMarkdown: z.string(),
    clock: hybridClockSchema
  })),
  latestSequence: z.number().int().nonnegative()
});
export const pullResponseSchema = z.object({
  changes: z.array(syncChangeSchema),
  nextCursor: z.number().int().nonnegative(),
  hasMore: z.boolean()
});

export const canonicalChangeKindSchema = z.enum(["space", "item", "recovered-version"]);
export type CanonicalChangeKind = z.infer<typeof canonicalChangeKindSchema>;

const canonicalChangeBaseSchema = z.object({
  sequence: z.number().int().positive()
});

export const canonicalChangeSchema = z.union([
  canonicalChangeBaseSchema.extend({ kind: z.literal("space"), action: z.literal("upsert"), entity: spaceSchema }),
  canonicalChangeBaseSchema.extend({ kind: z.literal("item"), action: z.literal("upsert"), entity: canvasItemSchema }),
  canonicalChangeBaseSchema.extend({ kind: z.literal("recovered-version"), action: z.literal("upsert"), entity: recoveredVersionSchema }),
  canonicalChangeBaseSchema.extend({
    kind: canonicalChangeKindSchema,
    action: z.literal("remove"),
    entityId: z.string().min(1).max(160)
  })
]);
export type CanonicalChange = z.infer<typeof canonicalChangeSchema>;

export const syncV2SnapshotSchema = z.object({
  spaces: z.array(spaceSchema),
  items: z.array(canvasItemSchema),
  recoveredVersions: z.array(recoveredVersionSchema),
  latestCursor: z.number().int().nonnegative()
});
export type SyncV2Snapshot = z.infer<typeof syncV2SnapshotSchema>;

export const clientCapabilitySchema = z.enum(["spreadsheet-v1", "locked-notes-v1", "locked-folders-v1"]);
export type ClientCapability = z.infer<typeof clientCapabilitySchema>;
export const clientCapabilitiesSchema = z.array(clientCapabilitySchema).max(16).default([]);

export const syncV2PushRequestSchema = z.object({
  deviceId: z.string().min(1).max(128),
  cursor: z.number().int().nonnegative(),
  operations: z.array(syncOperationSchema).max(500),
  capabilities: clientCapabilitiesSchema
});
export type SyncV2PushRequest = z.infer<typeof syncV2PushRequestSchema>;

export const syncOperationResultSchema = z.object({
  opId: z.string().min(1).max(128),
  status: z.enum(["applied", "duplicate", "rejected"]),
  errorCode: z.string().min(1).max(80).optional(),
  message: z.string().max(500).optional()
});
export type SyncOperationResult = z.infer<typeof syncOperationResultSchema>;

export const syncV2PushResponseSchema = z.object({
  results: z.array(syncOperationResultSchema),
  conflicts: z.array(recoveredVersionSchema),
  latestCursor: z.number().int().nonnegative()
});
export type SyncV2PushResponse = z.infer<typeof syncV2PushResponseSchema>;

export const syncV2PullResponseSchema = z.object({
  changes: z.array(canonicalChangeSchema),
  nextCursor: z.number().int().nonnegative(),
  headCursor: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  requiresBootstrap: z.boolean()
});
export type SyncV2PullResponse = z.infer<typeof syncV2PullResponseSchema>;

export const syncCheckpointSchema = z.object({
  latestCursor: z.number().int().nonnegative(),
  contentHash: z.string().regex(/^[a-f0-9]{64}$/)
});
export type SyncCheckpoint = z.infer<typeof syncCheckpointSchema>;

export const syncTransportErrorSchema = z.object({
  retryable: z.boolean(),
  status: z.number().int().nullable(),
  code: z.string().min(1).max(80),
  message: z.string().min(1).max(500),
  correlationId: z.string().min(1).max(200).nullable(),
  retryAt: z.number().int().nonnegative().nullable()
});
export type SyncTransportError = z.infer<typeof syncTransportErrorSchema>;

export const desktopVersionSchema = z.string().regex(/^(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)$/, "Desktop versions must use major.minor.patch");
export const desktopUpdateTargetSchema = z.enum(["win32-x64", "darwin-universal", "linux-x64"]);
export type DesktopUpdateTarget = z.infer<typeof desktopUpdateTargetSchema>;

export const desktopUpdateAssetFormats = {
  "win32-x64": { extension: "exe", contentType: "application/vnd.microsoft.portable-executable" },
  "darwin-universal": { extension: "dmg", contentType: "application/x-apple-diskimage" },
  "linux-x64": { extension: "rpm", contentType: "application/x-rpm" }
} as const satisfies Record<DesktopUpdateTarget, { extension: string; contentType: string }>;

export const desktopUpdateAssetSchema = z.object({
  filename: z.string().min(1).max(180).regex(/^[A-Za-z0-9][A-Za-z0-9._ -]*$/, "Update filenames may not contain path separators"),
  byteLength: z.number().int().positive().max(1024 * 1024 * 1024),
  sha256: z.string().regex(/^[a-f0-9]{64}$/)
}).strict();
export type DesktopUpdateAsset = z.infer<typeof desktopUpdateAssetSchema>;

export const desktopUpdateManifestSchema = z.object({
  schemaVersion: z.literal(1),
  version: desktopVersionSchema,
  publishedAt: z.iso.datetime(),
  assets: z.object({
    "win32-x64": desktopUpdateAssetSchema,
    "darwin-universal": desktopUpdateAssetSchema,
    // Releases published before Fedora support have no Linux installer.
    "linux-x64": desktopUpdateAssetSchema.optional()
  }).strict()
}).strict();
export type DesktopUpdateManifest = z.infer<typeof desktopUpdateManifestSchema>;

export const desktopUpdateCatalogResponseSchema = z.discriminatedUnion("available", [
  z.object({ available: z.literal(false) }).strict(),
  z.object({
    available: z.literal(true),
    version: desktopVersionSchema,
    publishedAt: z.iso.datetime(),
    target: desktopUpdateTargetSchema,
    asset: desktopUpdateAssetSchema
  }).strict()
]);
export type DesktopUpdateCatalogResponse = z.infer<typeof desktopUpdateCatalogResponseSchema>;

export interface DesktopUpdateInfo {
  version: string;
  publishedAt: string;
  target: DesktopUpdateTarget;
  asset: DesktopUpdateAsset;
}

export type DesktopUpdateState =
  | { phase: "idle" | "checking" | "current"; currentVersion: string }
  | { phase: "available" | "ready" | "installing" | "installed"; currentVersion: string; update: DesktopUpdateInfo }
  | { phase: "downloading"; currentVersion: string; update: DesktopUpdateInfo; receivedBytes: number; totalBytes: number }
  | { phase: "error"; currentVersion: string; update?: DesktopUpdateInfo; message: string };

export const authCheckResponseSchema = z.object({
  authenticated: z.literal(true),
  userId: z.string().min(1).max(256),
  legacyLocalProfile: z.boolean()
});
export type AuthCheckResponse = z.infer<typeof authCheckResponseSchema>;

export const accountStorageUsageSchema = z.object({
  storedBytes: z.number().int().nonnegative(),
  reservedBytes: z.number().int().nonnegative(),
  usedBytes: z.number().int().nonnegative(),
  limitBytes: z.number().int().positive()
});
export type AccountStorageUsage = z.infer<typeof accountStorageUsageSchema>;

export const multipartAssetCreateRequestSchema = z.object({
  assetId: z.string().min(1).max(128),
  filename: z.string().min(1).max(512),
  mimeType: z.string().min(1).max(200),
  byteLength: z.number().int().positive().max(500 * 1024 * 1024)
});
export const multipartAssetCreateResponseSchema = z.object({
  assetId: z.string().min(1).max(128),
  uploadId: z.string().max(512),
  partSize: z.number().int().positive().max(8 * 1024 * 1024),
  status: z.enum(["uploading", "ready"])
});
export const multipartPartResponseSchema = z.object({
  partNumber: z.number().int().min(1).max(10_000),
  etag: z.string().min(1).max(256)
});
export const multipartCompleteRequestSchema = z.object({
  parts: z.array(multipartPartResponseSchema).min(1).max(10_000)
});
export const clipEnqueueRequestSchema = z.object({
  clipId: z.string().min(1).max(128),
  itemId: z.string().min(1).max(128),
  url: z.string().url().max(4096)
});

/**
 * Returns a collision-resistant, URL-safe key for an authenticated account.
 * The original Clerk ID is intentionally not exposed in storage paths.
 */
export async function deriveAccountStorageKey(userId: string): Promise<string> {
  if (userId.length < 1) throw new TypeError("A Clerk user ID is required");
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(userId)));
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
  let encoded = "";
  for (let index = 0; index < digest.length; index += 3) {
    const first = digest[index] ?? 0;
    const second = digest[index + 1];
    const third = digest[index + 2];
    encoded += alphabet[first >> 2];
    encoded += alphabet[((first & 0x03) << 4) | ((second ?? 0) >> 4)];
    if (second !== undefined) encoded += alphabet[((second & 0x0f) << 2) | ((third ?? 0) >> 6)];
    if (third !== undefined) encoded += alphabet[third & 0x3f];
  }
  return encoded;
}

export function compareClocks(left: HybridClock, right: HybridClock): -1 | 0 | 1 {
  if (left.wallTime !== right.wallTime) return left.wallTime < right.wallTime ? -1 : 1;
  if (left.counter !== right.counter) return left.counter < right.counter ? -1 : 1;
  if (left.deviceId === right.deviceId) return 0;
  return left.deviceId < right.deviceId ? -1 : 1;
}

const editableSpaceFields = new Set(["title", "theme", "starred", "deletedAt"]);

export function mergeSpaceOperation(current: Space, operation: SyncOperation): Space {
  const next: Space = structuredClone(current);
  const entity = operation.patch["entity"];
  const incoming = entity && typeof entity === "object" && !Array.isArray(entity)
    ? spaceSchema.safeParse(entity)
    : undefined;
  const patch = incoming?.success
    ? Object.fromEntries([...editableSpaceFields].map((field) => [field, incoming.data[field as keyof Space]]))
    : operation.patch;

  for (const [field, value] of Object.entries(patch)) {
    if (!editableSpaceFields.has(field)) continue;
    const currentClock = current.fieldClocks[field];
    if (currentClock && compareClocks(operation.clock, currentClock) <= 0) continue;
    if (field === "title" && typeof value === "string") next.title = value.slice(0, 200);
    if (field === "theme" && (value === "light" || value === "dark" || value === "system")) next.theme = value;
    if (field === "starred" && typeof value === "boolean") next.starred = value;
    if (field === "deletedAt" && (value === null || (typeof value === "number" && Number.isInteger(value) && value >= 0))) next.deletedAt = value;
    next.fieldClocks[field] = operation.clock;
  }
  next.updatedAt = Math.max(next.updatedAt, operation.clock.wallTime);
  return spaceSchema.parse(next);
}

export function tickClock(
  deviceId: string,
  previous?: HybridClock,
  received?: HybridClock,
  now = Date.now()
): HybridClock {
  const wallTime = Math.max(now, previous?.wallTime ?? 0, received?.wallTime ?? 0);
  let counter = 0;
  if (previous?.wallTime === wallTime && received?.wallTime === wallTime) {
    counter = Math.max(previous.counter, received.counter) + 1;
  } else if (previous?.wallTime === wallTime) {
    counter = previous.counter + 1;
  } else if (received?.wallTime === wallTime) {
    counter = received.counter + 1;
  }
  return { wallTime, counter, deviceId };
}

export interface MergeConflict {
  entityId: string;
  losingMarkdown: string;
  clock: HybridClock;
}

export interface ItemMergeResult {
  item: CanvasItem;
  conflict: MergeConflict | null;
}

const editableItemFields = new Set([
  "x", "y", "width", "height", "rotation", "zIndex", "parentStackId", "props", "deletedAt"
]);

export function mergeItemOperation(current: CanvasItem, operation: SyncOperation): ItemMergeResult {
  const next: CanvasItem = structuredClone(current);
  let conflict: MergeConflict | null = null;

  for (const [field, value] of Object.entries(operation.patch)) {
    if (!editableItemFields.has(field)) continue;
    const currentClock = current.fieldClocks[field];

    if (field === "props") {
      const parsed = itemPropsSchema.safeParse(value);
      if (!parsed.success || parsed.data.kind !== current.type) continue;
      const currentMarkdown = "markdown" in current.props ? current.props.markdown : null;
      const incomingMarkdown = "markdown" in parsed.data ? parsed.data.markdown : null;
      // A recovered copy of a locked body would be stored unencrypted, so locked edits stay last-writer-wins.
      const involvesLockedBody = isLockedItem(current) || isLockedItem({ props: parsed.data });
      const isConcurrentMarkdownEdit =
        !involvesLockedBody &&
        operation.baseRevision !== null &&
        operation.baseRevision < (currentClock?.wallTime ?? current.updatedAt) &&
        currentMarkdown !== null &&
        incomingMarkdown !== null &&
        currentMarkdown !== incomingMarkdown;
      if (currentClock && compareClocks(operation.clock, currentClock) <= 0) {
        if (isConcurrentMarkdownEdit && incomingMarkdown !== null) conflict = { entityId: current.id, losingMarkdown: incomingMarkdown, clock: operation.clock };
        continue;
      }
      if (isConcurrentMarkdownEdit && currentMarkdown !== null) conflict = { entityId: current.id, losingMarkdown: currentMarkdown, clock: currentClock ?? operation.clock };
      next.props = sealLockedProps(parsed.data);
    } else {
      if (currentClock && compareClocks(operation.clock, currentClock) <= 0) continue;
      if (field === "deletedAt") {
        if (value === null || (typeof value === "number" && Number.isInteger(value) && value >= 0)) {
          next.deletedAt = value;
        }
      } else if (field === "parentStackId") {
        if (value === null || typeof value === "string") next.parentStackId = value;
      } else if (field === "zIndex") {
        if (typeof value === "number" && Number.isInteger(value)) next.zIndex = value;
      } else if (typeof value === "number" && Number.isFinite(value)) {
        if (field === "x" || field === "y" || field === "width" || field === "height" || field === "rotation") {
          next[field] = value;
        }
      }
    }
    next.fieldClocks[field] = operation.clock;
  }

  next.updatedAt = Math.max(next.updatedAt, operation.clock.wallTime);
  return { item: canvasItemSchema.parse(next), conflict };
}

export function mergeClipOperation(current: CanvasItem, operation: SyncOperation): CanvasItem {
  // A sealed clip's capture details are encrypted; the queue must not write them back in the clear.
  if (operation.entityType !== "clip" || current.props.kind !== "web-clip" || current.props.lock) return current;
  if (operation.patch["itemId"] !== current.id) return current;
  const status = operation.patch["status"];
  const previewAssetId = operation.patch["previewAssetId"];
  return canvasItemSchema.parse({
    ...current,
    props: {
      ...current.props,
      title: typeof operation.patch["title"] === "string" ? operation.patch["title"] : current.props.title,
      description: typeof operation.patch["description"] === "string" ? operation.patch["description"] : current.props.description,
      siteName: typeof operation.patch["siteName"] === "string" ? operation.patch["siteName"] : current.props.siteName,
      status: status === "ready" || status === "failed" ? status : current.props.status,
      previewAssetId: typeof previewAssetId === "string" ? previewAssetId : current.props.previewAssetId
    },
    fieldClocks: { ...current.fieldClocks, props: operation.clock },
    updatedAt: Math.max(current.updatedAt, operation.clock.wallTime)
  });
}

export function itemLock(props: ItemProps): NoteLock | undefined {
  return "lock" in props ? props.lock : undefined;
}

export function isLockedItem(item: Pick<CanvasItem, "props">): boolean {
  return itemLock(item.props) !== undefined;
}

/** True for content encrypted as part of a locked folder. */
export function isSealedItem(item: Pick<CanvasItem, "props">): boolean {
  return itemLock(item.props)?.scope === "item";
}

export function isLockedFolder(item: Pick<CanvasItem, "props">): boolean {
  return item.props.kind === "stack" && item.props.lock?.scope === "folder";
}

/** Fields a folder seal encrypts, and the stand-ins stored and synced in their place. */
const sealedPlaceholders: Partial<Record<ItemType, Record<string, unknown>>> = {
  "sticky-note": { markdown: "", plainText: "" },
  document: { title: "", markdown: "", plainText: "" },
  label: { text: "" },
  image: { filename: "sealed", mimeType: SEALED_ASSET_MIME_TYPE, alt: "" },
  video: { filename: "sealed", mimeType: SEALED_ASSET_MIME_TYPE, posterAssetId: null },
  pdf: { filename: "sealed" },
  spreadsheet: { filename: "sealed" },
  "web-clip": { url: "https://sealed.invalid/", title: "", description: "", siteName: "" },
  "date-event": { title: "Locked", date: "2000-01-01", time: undefined }
};

export function canSealItem(item: Pick<CanvasItem, "type">): boolean {
  return sealedPlaceholders[item.type] !== undefined;
}

/** The identifying fields of an item's content, which a folder seal moves into ciphertext. */
export function sealedItemSecrets(props: ItemProps): Record<string, unknown> {
  const placeholders = sealedPlaceholders[props.kind] ?? {};
  const source = props as Record<string, unknown>;
  return Object.fromEntries(Object.keys(placeholders).filter((field) => field !== "posterAssetId" && source[field] !== undefined).map((field) => [field, source[field]]));
}

/** A locked body lives only in its ciphertext, so any readable copy is dropped. */
export function sealLockedProps<T extends ItemProps>(props: T): T {
  const lock = itemLock(props);
  if (!lock || lock.scope === "folder") return props;
  if (lock.scope === undefined) {
    return props.kind === "sticky-note" || props.kind === "document" ? { ...props, markdown: "", plainText: "" } : props;
  }
  const sealed: Record<string, unknown> = { ...props };
  for (const [field, placeholder] of Object.entries(sealedPlaceholders[props.kind] ?? {})) {
    if (placeholder === undefined) delete sealed[field];
    else sealed[field] = placeholder;
  }
  return sealed as T;
}

/**
 * Points a queued item operation at a now-locked body so the outbox stops holding the readable
 * text it was created with. The later lock operation still decides the synced result.
 */
export function withoutReadableBody(operation: SyncOperation, lockedProps: ItemProps): SyncOperation {
  if (operation.entityType !== "item") return operation;
  const patch = { ...operation.patch };
  if ("props" in patch) patch["props"] = lockedProps;
  const entity = patch["entity"];
  if (entity && typeof entity === "object" && !Array.isArray(entity) && "props" in entity) patch["entity"] = { ...entity, props: lockedProps };
  return { ...operation, patch };
}

export function makeId(prefix: string): string {
  return `${prefix}_${crypto.randomUUID()}`;
}

export function markdownToPlainText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*$/gm, " ")
    .replace(/\s*\|\s*/g, " ")
    .replace(/(?:^|\s)[#>*_`~-]+/gm, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function canonicalSnapshotJson(snapshot: Pick<SyncV2Snapshot, "spaces" | "items" | "recoveredVersions">): string {
  const spaces = snapshot.spaces.map((space) => spaceSchema.parse(space)).sort((left, right) => left.id.localeCompare(right.id));
  const items = snapshot.items.map((unsafeItem) => {
    const item = canvasItemSchema.parse(unsafeItem);
    if (item.props.kind === "image" || item.props.kind === "video" || item.props.kind === "pdf" || item.props.kind === "spreadsheet") {
      const { localUrl: _localUrl, ...props } = item.props;
      return { ...item, props };
    }
    if (item.props.kind === "web-clip") {
      const { previewUrl: _previewUrl, ...props } = item.props;
      return { ...item, props };
    }
    return item;
  }).sort((left, right) => left.id.localeCompare(right.id));
  const recoveredVersions = snapshot.recoveredVersions
    .map((version) => recoveredVersionSchema.parse(version))
    .sort((left, right) => left.id.localeCompare(right.id));
  return JSON.stringify(sortJsonObjectKeys({ spaces, items, recoveredVersions }));
}

function sortJsonObjectKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortJsonObjectKeys);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)).map(([key, child]) => [key, sortJsonObjectKeys(child)]));
}

export async function hashCanonicalSnapshot(snapshot: Pick<SyncV2Snapshot, "spaces" | "items" | "recoveredVersions">): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonicalSnapshotJson(snapshot)));
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

// Kept outside the content snapshot: reopening a note must not count as editing it.
export const workingSessionSchema = z.object({
  spaceId: z.string().min(1).max(128),
  itemId: z.string().min(1).max(128).nullable(),
  cursor: z.number().int().nonnegative().default(0),
  scrollTop: z.number().finite().nonnegative().default(0),
  updatedAt: z.number().int().nonnegative(),
  deviceId: z.string().min(1).max(128)
}).strict();
export type WorkingSession = z.infer<typeof workingSessionSchema>;
